import React from 'react';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import DateRange from 'components/Form/DateRange';
import NotApplicable from 'components/Form/NotApplicable';
import Textarea from 'components/Form/Textarea';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import { Dispatch } from 'redux';
import { LegalAssociationsTerroristOrganizationItemModel } from 'forms/sections/legal/associations/terroristOrganization/types';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonAddressBooks,
  CommonOnError,
  CommonOnUpdate,
  CommonRequired,
  CommonScrollIntoView,
} from '../../../../types/props';

interface Props
  extends Partial<LegalAssociationsTerroristOrganizationItemModel>,
  ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  dispatch?: Dispatch
}

export default class TerroristOrganizationItem extends ValidationElement<Props> {
  update = (queue: Partial<LegalAssociationsTerroristOrganizationItemModel>) => {
    this.props.onUpdate({
      Organization: this.props.Organization,
      Address: this.props.Address,
      Dates: this.props.Dates,
      PositionsNotApplicable: this.props.PositionsNotApplicable,
      Positions: this.props.Positions,
      ContributionsNotApplicable: this.props.ContributionsNotApplicable,
      Contributions: this.props.Contributions,
      Reasons: this.props.Reasons,
      ...queue,
    });
  };

  updateOrganization = (
    values: LegalAssociationsTerroristOrganizationItemModel['Organization']
  ) => {
    this.update({
      Organization: values,
    });
  };

  updateAddress = (values: LegalAssociationsTerroristOrganizationItemModel['Address']) => {
    this.update({
      Address: values,
    });
  };

  updateDates = (values: LegalAssociationsTerroristOrganizationItemModel['Dates']) => {
    this.update({
      Dates: values,
    });
  };

  updatePositionsNotApplicable = (
    values: LegalAssociationsTerroristOrganizationItemModel['PositionsNotApplicable']
  ) => {
    this.update({
      Positions: values.applicable ? this.props.Positions : undefined,
      PositionsNotApplicable: values,
    });
  };

  updatePositions = (values: LegalAssociationsTerroristOrganizationItemModel['Positions']) => {
    this.update({
      Positions: values,
    });
  };

  updateContributionsNotApplicable = (
    values: LegalAssociationsTerroristOrganizationItemModel['ContributionsNotApplicable']
  ) => {
    this.update({
      Contributions: values.applicable ? this.props.Contributions : undefined,
      ContributionsNotApplicable: values,
    });
  };

  updateContributions = (
    values: LegalAssociationsTerroristOrganizationItemModel['Contributions']
  ) => {
    this.update({
      Contributions: values,
    });
  };

  updateReasons = (values: LegalAssociationsTerroristOrganizationItemModel['Reasons']) => {
    this.update({
      Reasons: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;

    return (
      <div>
        <Field
          title={i18n.t('legal.associations.terrorist.heading.organization')}
          adjustFor="text"
          attribute={`${attribute}.Organization`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            name="Organization"
            attribute={`${attribute}.Organization`}
            {...this.props.Organization}
            onUpdate={this.updateOrganization}
            onError={this.props.onError}
            className="legal-associations-terrorist-organization"
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('legal.associations.terrorist.heading.address')}
          optional
          help="legal.associations.terrorist.help.address"
          adjustFor="address"
          attribute={`${attribute}.Address`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Location
            name="Address"
            attribute={`${attribute}.Address`}
            {...this.props.Address}
            onUpdate={this.updateAddress}
            onError={this.props.onError}
            className="legal-associations-terrorist-address"
            layout={Location.ADDRESS}
            geocode
            addressBooks={this.props.addressBooks}
            addressBook="Organization"
            dispatch={this.props.dispatch}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('legal.associations.terrorist.heading.dates')}
          help="legal.associations.terrorist.help.dates"
          adjustFor="daterange"
          attribute={`${attribute}.Dates`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            name="Dates"
            attribute={`${attribute}.Dates`}
            {...this.props.Dates}
            onUpdate={this.updateDates}
            onError={this.props.onError}
            minDateEqualTo
            className="legal-associations-terrorist-dates"
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>

        <Field
          title={i18n.t('legal.associations.terrorist.heading.positions')}
          adjustFor="text"
          attribute={`${attribute}.Positions`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <NotApplicable
            name="PositionsNotApplicable"
            attribute={`${attribute}.PositionsNotApplicable`}
            {...this.props.PositionsNotApplicable}
            className="legal-associations-terrorist-positions-na"
            onUpdate={this.updatePositionsNotApplicable}
            onError={this.props.onError}
            or={i18n.m('legal.associations.terrorist.para.or')}
            label={i18n.t('legal.associations.terrorist.label.noposition')}
            required={this.props.required}
            includeNAConfirmation={Boolean(this.props.Positions?.value)}
          >
            <Text
              name="Positions"
              attribute={`${attribute}.Positions`}
              {...this.props.Positions}
              onUpdate={this.updatePositions}
              onError={this.props.onError}
              className="legal-associations-terrorist-positions"
              required={this.props.required}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('legal.associations.terrorist.heading.contributions')}
          adjustFor="text"
          attribute={`${attribute}.Contributions`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <NotApplicable
            name="ContributionsNotApplicable"
            attribute={`${attribute}.ContributionsNotApplicable`}
            {...this.props.ContributionsNotApplicable}
            className="legal-associations-terrorist-contributions-na"
            onUpdate={this.updateContributionsNotApplicable}
            onError={this.props.onError}
            or={i18n.m('legal.associations.terrorist.para.or')}
            label={i18n.t('legal.associations.terrorist.label.nocontribs')}
            required={this.props.required}
            includeNAConfirmation={Boolean(this.props.Contributions?.value)}
          >
            <Text
              name="Contributions"
              attribute={`${attribute}.Contributions`}
              {...this.props.Contributions}
              onUpdate={this.updateContributions}
              onError={this.props.onError}
              className="legal-associations-terrorist-contributions"
              required={this.props.required}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('legal.associations.terrorist.heading.reasons')}
          adjustFor="textarea"
          attribute={`${attribute}.Reasons`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="Reasons"
            attribute={`${attribute}.Reasons`}
            {...this.props.Reasons}
            onUpdate={this.updateReasons}
            onError={this.props.onError}
            className="legal-associations-terrorist-reasons"
            required={this.props.required}
          />
        </Field>
      </div >
    );
  }
}

TerroristOrganizationItem.defaultProps = {
  PositionsNotApplicable: { applicable: true },
  ContributionsNotApplicable: { applicable: true },
  onUpdate: () => { },
  onError: (value, arr) => arr,
};
